import { PropsWithChildren, createContext, useContext, useMemo } from "react";

import type { LoadAdditionalItems } from "../components/filterable-gallery/src/types";

export interface FilterableGalleryLoaderRegistration {
    loader: LoadAdditionalItems;
}

interface FilterableGalleryLoaderContextValue {
    registerLoader: (
        registration: FilterableGalleryLoaderRegistration | null
    ) => void;
    currentRegistration: FilterableGalleryLoaderRegistration | null;
    extraItems: unknown[];
    setExtraItems: (items: unknown[]) => void;
}

const FilterableGalleryLoaderContext =
    createContext<FilterableGalleryLoaderContextValue | null>(null);

interface FilterableGalleryLoaderProviderProps {
    onLoaderRegistered?: (
        registration: FilterableGalleryLoaderRegistration | null
    ) => void;
    currentRegistration?: FilterableGalleryLoaderRegistration | null;
    extraItems?: unknown[];
    setExtraItems?: (items: unknown[]) => void;
}

export const FilterableGalleryLoaderProvider = ({
    children,
    onLoaderRegistered = () => undefined,
    currentRegistration = null,
    extraItems = [],
    setExtraItems = () => undefined
}: PropsWithChildren<FilterableGalleryLoaderProviderProps>): JSX.Element => {
    const value = useMemo<FilterableGalleryLoaderContextValue>(
        () => ({
            registerLoader: onLoaderRegistered,
            currentRegistration,
            extraItems,
            setExtraItems
        }),
        [onLoaderRegistered, currentRegistration, extraItems, setExtraItems]
    );
    return (
        <FilterableGalleryLoaderContext.Provider value={value}>
            {children}
        </FilterableGalleryLoaderContext.Provider>
    );
};

export const useFilterableGalleryLoader =
    (): FilterableGalleryLoaderContextValue | null =>
        useContext(FilterableGalleryLoaderContext);

export const useIsInsideLoaderProvider = (): boolean =>
    useContext(FilterableGalleryLoaderContext) !== null;
